import { useCallback, useRef } from "react";
import type { MouseEvent as ReactMouseEvent, TouchEvent as ReactTouchEvent } from "react";

import { clampDragOffset, type AspectRatio, type DragOffset } from "./helpers";
import { useLatestRef } from "../../common/useLatestRef";

import { type FitImageResult } from "@/components/ImageUploader/utils";

type UseImageDragParams = {
  aspectRatio: AspectRatio | null;
  dragOffset: DragOffset;
  fittedImage: FitImageResult | null;
  onDragChange: (offset: DragOffset) => void;
  schedulePreviewUpdate: () => void;
  targetSize: number;
  viewportSize: number;
};

export const useImageDrag = ({
  aspectRatio,
  dragOffset,
  fittedImage,
  onDragChange,
  schedulePreviewUpdate,
  targetSize,
  viewportSize,
}: UseImageDragParams) => {
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);
  const dragStartOffsetRef = useRef<DragOffset | null>(null);

  const aspectRatioRef = useLatestRef(aspectRatio);
  const fittedImageRef = useLatestRef(fittedImage);
  const targetSizeRef = useLatestRef(targetSize);
  const viewportSizeRef = useLatestRef(viewportSize);
  const dragOffsetRef = useLatestRef(dragOffset);
  const schedulePreviewUpdateRef = useLatestRef(schedulePreviewUpdate);

  const handleDragStart = useCallback(
    (e: ReactMouseEvent | ReactTouchEvent) => {
      if (!fittedImageRef.current || aspectRatioRef.current === "square") {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
      const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;

      dragStartRef.current = { x: clientX, y: clientY };
      dragStartOffsetRef.current = { ...dragOffsetRef.current };

      const handleDocumentMouseMove = (docE: MouseEvent | TouchEvent) => {
        docE.preventDefault();
        const docClientX = "touches" in docE ? docE.touches[0].clientX : docE.clientX;
        const docClientY = "touches" in docE ? docE.touches[0].clientY : docE.clientY;

        if (
          !dragStartRef.current ||
          !dragStartOffsetRef.current ||
          !fittedImageRef.current ||
          !aspectRatioRef.current
        ) {
          return;
        }

        const deltaX = docClientX - dragStartRef.current.x;
        const deltaY = docClientY - dragStartRef.current.y;
        const scale = viewportSizeRef.current / targetSizeRef.current;
        const deltaXInTargetSize = deltaX / scale;
        const deltaYInTargetSize = deltaY / scale;

        const constrainedOffset = clampDragOffset(
          aspectRatioRef.current,
          fittedImageRef.current,
          targetSizeRef.current,
          {
            x: dragStartOffsetRef.current.x + deltaXInTargetSize,
            y: dragStartOffsetRef.current.y + deltaYInTargetSize,
          }
        );

        dragOffsetRef.current = constrainedOffset;
        onDragChange(constrainedOffset);
        schedulePreviewUpdateRef.current?.();
      };

      const handleDocumentMouseUp = () => {
        document.removeEventListener("mousemove", handleDocumentMouseMove as EventListener);
        document.removeEventListener("mouseup", handleDocumentMouseUp);
        document.removeEventListener("touchmove", handleDocumentMouseMove as EventListener);
        document.removeEventListener("touchend", handleDocumentMouseUp);
        dragStartRef.current = null;
        dragStartOffsetRef.current = null;
      };

      document.addEventListener("mousemove", handleDocumentMouseMove as EventListener);
      document.addEventListener("mouseup", handleDocumentMouseUp);
      document.addEventListener("touchmove", handleDocumentMouseMove as EventListener);
      document.addEventListener("touchend", handleDocumentMouseUp);
    },
    [
      aspectRatioRef,
      dragOffsetRef,
      fittedImageRef,
      onDragChange,
      schedulePreviewUpdateRef,
      targetSizeRef,
      viewportSizeRef,
    ]
  );

  return { handleDragStart };
};
